import { useAuthToken } from '@checkatrade/auth/cjs';
import { useIsAuthenticated, useSessionUserId } from 'lib/auth/auth.hooks';
import { getChatToken } from 'lib/consumer/chat/get-chat-token';
import { useState, useEffect, useCallback } from 'react';
import type { StreamChat, DefaultGenerics } from 'stream-chat';

type ChatState = {
  client: StreamChat<DefaultGenerics> | null;
  unreadCount?: number;
};

const initialState = {
  client: null,
  unreadCount: undefined,
} satisfies ChatState;

const requestIdleCallback =
  (typeof self !== 'undefined' &&
    self.requestIdleCallback &&
    self.requestIdleCallback.bind(window)) ||
  function (cb: IdleRequestCallback) {
    const start = Date.now();
    setTimeout(() => {
      cb({
        timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
        didTimeout: false,
      });
    }, 1);
  };

export const useLazyChat = () => {
  const [state, setState] = useState<ChatState>(initialState);
  const isAuthenticated = useIsAuthenticated();
  const userId = useSessionUserId();
  const authToken = useAuthToken();

  const initialiseChatClient = useCallback(
    async (token: string | null) => {
      if (!userId) return;
      if (!token) return;
      const streamChat = (await import('stream-chat')).StreamChat;
      const chatToken = await getChatToken(token);
      if (!chatToken || chatToken.error) return;

      const client = streamChat.getInstance(chatToken.apiKey);

      client.connectUser({ id: userId }, chatToken.accessToken);

      const unreadCount = (await client.getUnreadCount()).total_unread_count;

      setState({ client, unreadCount });
    },
    [userId],
  );

  useEffect(() => {
    if (authToken && isAuthenticated && !state.client) {
      requestIdleCallback(() => {
        initialiseChatClient(authToken);
      });
    }
  }, [isAuthenticated, authToken, state.client, initialiseChatClient]);

  return state;
};
